import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import BottomNav from '@/components/BottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Calculator, 
  Target,
  Calendar,
  TrendingUp,
  PiggyBank,
  Clock,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SavingsCalculator = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [targetAmount, setTargetAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount).replace('NGN', '₦');
  };

  const handleCalculate = async () => {
    const amount = parseFloat(targetAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid target amount');
      return;
    }

    setLoading(true);
    setPlans(null);
    setSelectedPlan(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/calculator/savings`,
        { target_amount: amount },
        { headers: getAuthHeaders() }
      );
      setPlans(response.data);
    } catch (error) {
      toast.error('Failed to calculate savings plans');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGoal = async () => {
    if (!selectedPlan) return;
    
    setLoading(true);
    try {
      // Create the savings goal directly from the calculator
      await axios.post(
        `${API_URL}/api/savings`,
        {
          name: `Savings Goal - ${formatCurrency(plans.target_amount)}`,
          target_amount: plans.target_amount,
          contribution_amount: selectedPlan.contribution_amount,
          frequency: selectedPlan.frequency,
          duration_periods: selectedPlan.duration_periods
        },
        { headers: getAuthHeaders() }
      );
      toast.success('Savings goal created successfully!');
      navigate('/savings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create savings goal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <ArrowLeft size={20} className="text-slate-700" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-[#0B1F3A]">Savings Calculator</h1>
            <p className="text-sm text-slate-500">Plan your savings journey</p>
          </div>
          <Calculator size={24} className="text-[#0B1F3A]" />
        </div>
      </div>

      <div className="p-4">
        {/* Target Amount Input */}
        <Card className="glass-card mb-6 border-2 border-[#0B1F3A]/20 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-bold text-[#0B1F3A] flex items-center gap-2">
              <Target size={22} className="text-[#0B1F3A]" />
              Set Your Target
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#0B1F3A] font-bold text-base">
                How much do you want to save?
              </Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold text-lg">₦</span>
                <Input
                  type="number"
                  placeholder="100,000"
                  value={targetAmount}
                  onChange={(e) => setTargetAmount(e.target.value)}
                  className="h-16 rounded-xl border-2 border-slate-400 focus:border-[#0B1F3A] text-slate-900 font-bold text-2xl pl-10"
                  data-testid="target-amount-input"
                />
              </div>
              <p className="text-sm text-slate-600 font-medium">
                Enter your savings goal and we'll show you different plans to achieve it
              </p>
            </div>

            <Button 
              onClick={handleCalculate}
              disabled={loading || !targetAmount}
              className="w-full h-14 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-lg"
              data-testid="calculate-btn"
            >
              {loading ? 'Calculating...' : 'Calculate Plans'}
            </Button>
          </CardContent>
        </Card>

        {/* Results */}
        {plans && (
          <div className="space-y-4">
            {/* Summary */}
            <Card className="glass-card border-l-4 border-l-emerald-500 shadow-lg">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 font-medium">Target Amount</p>
                    <p className="text-2xl font-black text-[#0B1F3A]">{formatCurrency(plans.target_amount)}</p>
                  </div>
                  <div className="p-3 rounded-full bg-emerald-100">
                    <TrendingUp size={24} className="text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Daily Plans */}
            <div>
              <h3 className="font-bold text-[#0B1F3A] text-lg mb-3 flex items-center gap-2">
                <Clock size={18} />
                Daily Savings Plans
              </h3>
              <div className="space-y-3">
                {plans.plans.filter(p => p.frequency === 'daily').map((plan) => (
                  <Card 
                    key={plan.id}
                    className={`glass-card cursor-pointer transition-all ${
                      selectedPlan?.id === plan.id 
                        ? 'border-2 border-emerald-500 bg-emerald-50' 
                        : 'border-2 border-slate-200 hover:border-[#0B1F3A]/50'
                    }`}
                    onClick={() => setSelectedPlan(plan)}
                    data-testid={`plan-${plan.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-blue-100 text-blue-700 font-bold">{plan.label}</Badge>
                          {selectedPlan?.id === plan.id && (
                            <CheckCircle size={18} className="text-emerald-600" />
                          )}
                        </div>
                        <p className="text-lg font-black text-[#0B1F3A]">
                          {formatCurrency(plan.contribution_amount)}/day
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="p-2 rounded-lg bg-slate-100">
                          <p className="text-slate-600 font-medium">Duration</p>
                          <p className="font-bold text-slate-800">{plan.duration_label}</p>
                        </div>
                        <div className="p-2 rounded-lg bg-slate-100">
                          <p className="text-slate-600 font-medium">Completion</p>
                          <p className="font-bold text-slate-800">{plan.completion_date}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Weekly Plans */}
            <div>
              <h3 className="font-bold text-[#0B1F3A] text-lg mb-3 flex items-center gap-2">
                <Calendar size={18} />
                Weekly Savings Plans
              </h3>
              <div className="space-y-3">
                {plans.plans.filter(p => p.frequency === 'weekly').map((plan) => (
                  <Card 
                    key={plan.id}
                    className={`glass-card cursor-pointer transition-all ${
                      selectedPlan?.id === plan.id 
                        ? 'border-2 border-emerald-500 bg-emerald-50' 
                        : 'border-2 border-slate-200 hover:border-[#0B1F3A]/50'
                    }`}
                    onClick={() => setSelectedPlan(plan)}
                    data-testid={`plan-${plan.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-purple-100 text-purple-700 font-bold">{plan.label}</Badge>
                          {selectedPlan?.id === plan.id && (
                            <CheckCircle size={18} className="text-emerald-600" />
                          )}
                        </div>
                        <p className="text-lg font-black text-[#0B1F3A]">
                          {formatCurrency(plan.contribution_amount)}/week
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="p-2 rounded-lg bg-slate-100">
                          <p className="text-slate-600 font-medium">Duration</p>
                          <p className="font-bold text-slate-800">{plan.duration_label}</p>
                        </div>
                        <div className="p-2 rounded-lg bg-slate-100">
                          <p className="text-slate-600 font-medium">Completion</p>
                          <p className="font-bold text-slate-800">{plan.completion_date}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Monthly Plans */}
            {plans.plans.filter(p => p.frequency === 'monthly').length > 0 && (
              <div>
                <h3 className="font-bold text-[#0B1F3A] text-lg mb-3 flex items-center gap-2">
                  <PiggyBank size={18} />
                  Monthly Savings Plans
                </h3>
                <div className="space-y-3">
                  {plans.plans.filter(p => p.frequency === 'monthly').map((plan) => (
                    <Card 
                      key={plan.id}
                      className={`glass-card cursor-pointer transition-all ${
                        selectedPlan?.id === plan.id 
                          ? 'border-2 border-emerald-500 bg-emerald-50' 
                          : 'border-2 border-slate-200 hover:border-[#0B1F3A]/50'
                      }`}
                      onClick={() => setSelectedPlan(plan)}
                      data-testid={`plan-${plan.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-amber-100 text-amber-700 font-bold">{plan.label}</Badge>
                            {selectedPlan?.id === plan.id && (
                              <CheckCircle size={18} className="text-emerald-600" />
                            )}
                          </div>
                          <p className="text-lg font-black text-[#0B1F3A]">
                            {formatCurrency(plan.contribution_amount)}/month
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div className="p-2 rounded-lg bg-slate-100">
                            <p className="text-slate-600 font-medium">Duration</p>
                            <p className="font-bold text-slate-800">{plan.duration_label}</p>
                          </div>
                          <div className="p-2 rounded-lg bg-slate-100">
                            <p className="text-slate-600 font-medium">Completion</p>
                            <p className="font-bold text-slate-800">{plan.completion_date}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Selected Plan Details */}
            {selectedPlan && (
              <Card className="glass-card border-2 border-emerald-400 bg-emerald-50 shadow-lg">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-bold text-emerald-800 flex items-center gap-2">
                    <CheckCircle size={20} />
                    Selected Plan: {selectedPlan.label}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-xl bg-white">
                      <p className="text-sm text-slate-600 font-medium">Save</p>
                      <p className="text-xl font-black text-emerald-700">
                        {formatCurrency(selectedPlan.contribution_amount)}
                      </p>
                      <p className="text-xs text-slate-500">per {selectedPlan.frequency === 'daily' ? 'day' : selectedPlan.frequency === 'weekly' ? 'week' : 'month'}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-white">
                      <p className="text-sm text-slate-600 font-medium">Total</p>
                      <p className="text-xl font-black text-emerald-700">
                        {formatCurrency(selectedPlan.total_saved)}
                      </p>
                      <p className="text-xs text-slate-500">after {selectedPlan.duration_label}</p>
                    </div>
                  </div>
                  
                  <div className="p-3 rounded-xl bg-white">
                    <p className="text-sm text-slate-600 font-medium mb-2">Withdrawal Charges</p>
                    <div className="flex justify-between text-sm">
                      <span className="text-amber-700 font-medium">Early:</span>
                      <span className="font-bold text-amber-800">{formatCurrency(selectedPlan.early_withdrawal_charge)}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-emerald-700 font-medium">Completed:</span>
                      <span className="font-bold text-emerald-800">{formatCurrency(selectedPlan.completed_withdrawal_charge)}</span>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1">Rates depend on your plan. Premium and Pro enjoy lower fees.</p>
                  </div>

                  <Button 
                    onClick={handleCreateGoal}
                    disabled={loading}
                    className="w-full h-14 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-lg"
                    data-testid="create-goal-btn"
                  >
                    {loading ? 'Creating...' : 'Create This Savings Goal'}
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Info Section */}
        {!plans && (
          <div className="mt-6 p-4 rounded-2xl bg-[#0B1F3A] text-white">
            <div className="flex items-start gap-3">
              <Calculator size={24} className="text-white mt-0.5" />
              <div>
                <p className="font-bold text-white text-lg">How It Works</p>
                <ul className="mt-2 space-y-2 text-white font-medium">
                  <li>1. Enter your target savings amount</li>
                  <li>2. View different daily & weekly plans</li>
                  <li>3. Choose a plan that fits your budget</li>
                  <li>4. Create your savings goal instantly</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default SavingsCalculator;
